import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Switch, FormControlLabel,
  IconButton, Chip, CircularProgress, Alert, Tabs, Tab
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';

const emptyCustom = {
  label: '',
  key: '',
  unit: '',
  description: '',
  default_enabled: true,
  default_value: 0,
  default_price: 0
};

const PricingMetricsSettings = () => {
  const { can } = usePermissions();
  const canEdit = can('settings', 'edit');
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [standard, setStandard] = useState([]);
  const [custom, setCustom] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(emptyCustom);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/catalog/pricing/metrics');
      const data = res.data?.data || {};
      setStandard(data.standard || []);
      setCustom(data.custom || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load pricing metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStandardRow = (index, patch) => {
    setStandard((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const saveStandard = async () => {
    setSaving(true);
    try {
      const res = await api.put('/catalog/pricing/metrics/standard', { standard });
      setStandard(res.data?.data?.standard || standard);
      setCustom(res.data?.data?.custom || custom);
      toast.success('Standard metrics saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save standard metrics');
    } finally {
      setSaving(false);
    }
  };

  const openCreateCustom = () => {
    setEditingKey(null);
    setForm(emptyCustom);
    setDialogOpen(true);
  };

  const openEditCustom = (metric) => {
    setEditingKey(metric.key);
    setForm({
      label: metric.label,
      key: metric.key,
      unit: metric.unit || '',
      description: metric.description || '',
      default_enabled: metric.default_enabled !== false,
      default_value: metric.default_value || 0,
      default_price: metric.default_price || 0
    });
    setDialogOpen(true);
  };

  const saveCustom = async () => {
    try {
      let res;
      if (editingKey) {
        res = await api.put(`/catalog/pricing/metrics/custom/${editingKey}`, form);
        toast.success('Custom metric updated');
      } else {
        res = await api.post('/catalog/pricing/metrics/custom', form);
        toast.success('Custom metric added');
      }
      setStandard(res.data?.data?.standard || standard);
      setCustom(res.data?.data?.custom || custom);
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save custom metric');
    }
  };

  const deleteCustom = async (metric) => {
    if (!window.confirm(`Delete custom metric "${metric.label}"?`)) return;
    try {
      const res = await api.delete(`/catalog/pricing/metrics/custom/${metric.key}`);
      setStandard(res.data?.data?.standard || standard);
      setCustom(res.data?.data?.custom || custom);
      toast.success('Custom metric deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete custom metric');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Pricing Metrics</Typography>
          <Typography variant="body2" color="text.secondary">
            Define standard and custom metrics. Pricing plans pull metrics from this catalog.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Standard</strong> metrics are built-in (database size, bandwidth, users, etc.).
        <strong> Custom</strong> metrics can be added by administrators. Both appear when building a Pricing plan.
      </Alert>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 1 }}>
          <Tab label="Pricing Metrics (Standard)" />
          <Tab label="Pricing Metrics (Custom)" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>Standard Metrics</Typography>
            {canEdit && (
              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveStandard} disabled={saving}>
                {saving ? 'Saving…' : 'Save Standard Metrics'}
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Default Enabled</TableCell>
                  <TableCell>Default Value</TableCell>
                  <TableCell>Default Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {standard.map((metric, index) => (
                  <TableRow key={metric.key}>
                    <TableCell>
                      <Typography fontWeight={600}>{metric.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        <code>{metric.key}</code> · {metric.description}
                      </Typography>
                      <Chip size="small" label="Locked" sx={{ ml: 1 }} />
                    </TableCell>
                    <TableCell>{metric.unit}</TableCell>
                    <TableCell>
                      <Switch
                        checked={Boolean(metric.default_enabled)}
                        onChange={(e) => updateStandardRow(index, { default_enabled: e.target.checked })}
                        disabled={!canEdit}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={metric.default_value}
                        onChange={(e) => updateStandardRow(index, { default_value: e.target.value })}
                        disabled={!canEdit}
                        sx={{ width: 110 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={metric.default_price}
                        onChange={(e) => updateStandardRow(index, { default_price: e.target.value })}
                        disabled={!canEdit}
                        sx={{ width: 110 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>Custom Metrics</Typography>
            {canEdit && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateCustom}>
                Add Custom Metric
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Default Enabled</TableCell>
                  <TableCell>Default Value</TableCell>
                  <TableCell>Default Price</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {custom.map((metric) => (
                  <TableRow key={metric.key}>
                    <TableCell>
                      <Typography fontWeight={600}>{metric.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        <code>{metric.key}</code> · {metric.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{metric.unit}</TableCell>
                    <TableCell>{metric.default_enabled ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{metric.default_value}</TableCell>
                    <TableCell>{metric.default_price}</TableCell>
                    <TableCell align="right">
                      {canEdit && (
                        <>
                          <IconButton size="small" onClick={() => openEditCustom(metric)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => deleteCustom(metric)}><DeleteIcon fontSize="small" /></IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!custom.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No custom metrics yet. Add metrics that are unique to your business.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingKey ? 'Edit Custom Metric' : 'Add Custom Metric'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Label"
              required
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              fullWidth
            />
            {!editingKey && (
              <TextField
                label="Key (optional)"
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
                helperText="Auto-generated from label if blank"
                fullWidth
              />
            )}
            <TextField label="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} fullWidth />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              type="number"
              label="Default Value"
              value={form.default_value}
              onChange={(e) => setForm({ ...form, default_value: e.target.value })}
              fullWidth
            />
            <TextField
              type="number"
              label="Default Price"
              value={form.default_price}
              onChange={(e) => setForm({ ...form, default_price: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.default_enabled)}
                  onChange={(e) => setForm({ ...form, default_enabled: e.target.checked })}
                />
              }
              label="Enabled by default in new plans"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveCustom} disabled={!form.label}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingMetricsSettings;
